'use client';

import * as React from 'react';

import { cn } from '@/lib/util/cn';

/**
 * Segmented pill control — for small, mutually exclusive view switches
 * (Open / Closed / All, source filters). The active segment carries the
 * brand gradient. Keyboard: arrow keys move between segments.
 */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
  className,
  ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: React.ReactNode; count?: number }[];
  size?: 'sm' | 'md';
  className?: string;
  ariaLabel?: string;
}): React.ReactElement {
  const onKey = (e: React.KeyboardEvent) => {
    const i = options.findIndex((o) => o.value === value);
    if (e.key === 'ArrowRight' && i < options.length - 1) {onChange(options[i + 1]!.value);}
    if (e.key === 'ArrowLeft' && i > 0) {onChange(options[i - 1]!.value);}
  };
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={onKey}
      className={cn(
        'inline-flex items-center gap-1 rounded-md bg-surface-inset p-1',
        size === 'sm' ? 'h-9' : 'h-10',
        className,
      )}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex h-full items-center gap-1.5 rounded font-medium transition-colors duration-fast',
              size === 'sm' ? 'px-2.5 text-xs' : 'px-3 text-sm',
              active ? 'bg-surface text-fg shadow-xs' : 'text-fg-muted hover:text-fg',
            )}
          >
            {o.label}
            {o.count !== undefined ? (
              <span
                className={cn(
                  'rounded-full px-1.5 py-px font-tnum text-2xs tabular-nums',
                  active ? 'bg-surface-inset text-fg-muted' : 'text-fg-subtle',
                )}
              >
                {o.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
